// Motion pieces for the marketing pages: rotating hero word, marquee, and the large
// "bento" boxes whose mock UIs play on their own while they are on screen.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUp, Check, FileText, PenLine, Sparkles } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrationByKey, integrations } from "@/data/site";

const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);

/** Steps 0..count-1 on a timer, but only while `active`. */
function useLoop(count: number, ms: number, active: boolean) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setI((v) => (v + 1) % count), ms);
    return () => clearInterval(t);
  }, [count, ms, active]);
  return i;
}

function usePlaying<T extends Element>() {
  const ref = useRef<T>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const reduce = useReducedMotion();
  return { ref, playing: inView && !reduce && !isPrerender() };
}

/* ── Hero: a word that changes inside a soft pill ─────────────────────────── */
export const RotatingWord = ({ words }: { words: string[] }) => {
  const reduce = useReducedMotion();
  const i = useLoop(words.length, 2200, !reduce && !isPrerender());
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (
    <span className="relative mx-[0.12em] inline-flex items-center gap-[0.22em] rounded-[0.5em] bg-[hsl(216_90%_95%)] px-[0.32em] py-[0.02em] align-baseline">
      <span className="h-[0.34em] w-[0.34em] shrink-0 rounded-full bg-[hsl(216_84%_52%)]" />
      <span className="relative inline-block overflow-hidden text-left font-semibold tracking-[-0.03em]">
        <span className="invisible block">{longest}</span>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={words[i]}
            className="absolute inset-0"
            initial={{ y: "0.55em", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-0.55em", opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            {words[i]}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
};

/* ── Marquee ──────────────────────────────────────────────────────────────── */
export const Marquee = ({ children, speed = 38 }: { children: ReactNode; speed?: number }) => (
  <div className="cm-marquee relative overflow-hidden" style={{ ["--cm-marquee-s" as string]: `${speed}s` }}>
    <div className="cm-marquee-track flex w-max items-center gap-12 pr-12">
      {children}
      <span aria-hidden className="contents">{children}</span>
    </div>
  </div>
);

export const IntegrationMarquee = () => (
  <Marquee>
    {integrations.map((i) => (
      <span key={i.key} className="flex shrink-0 items-center gap-2.5 text-[17px] font-semibold tracking-[-0.01em] text-foreground">
        <img src={i.logo} alt="" className="h-6 w-6 object-contain opacity-80 grayscale" loading="lazy" /> {i.name}
      </span>
    ))}
  </Marquee>
);

/* ── Bento frame ──────────────────────────────────────────────────────────── */
export const Bento = ({
  eyebrow,
  title,
  href,
  children,
  wide = false,
}: {
  eyebrow: string;
  title: string;
  href: string;
  children: ReactNode;
  wide?: boolean;
}) => (
  <SmartLink
    href={href}
    className={`group relative flex overflow-hidden rounded-2xl bg-[hsl(40_9%_96%)] transition-colors duration-200 hover:bg-[hsl(40_9%_94%)] ${
      wide ? "flex-col lg:flex-row lg:items-stretch" : "flex-col"
    }`}
  >
    <div className={`flex items-start justify-between gap-4 p-6 sm:p-7 ${wide ? "lg:w-[38%] lg:flex-col lg:justify-start" : ""}`}>
      <div>
        <p className="text-[15px] text-foreground">{eyebrow}</p>
        <h3 className="mt-1.5 text-[1.35rem] font-semibold leading-snug tracking-[-0.02em] text-foreground sm:text-2xl">{title}</h3>
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-200 group-hover:translate-x-0.5 ${wide ? "lg:mt-5" : ""}`}>
        <ArrowRight className="h-4 w-4" />
      </span>
    </div>
    <div className={`relative min-h-[340px] flex-1 ${wide ? "lg:min-h-[360px]" : ""}`}>{children}</div>
  </SmartLink>
);

const Panel = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <div className={`rounded-t-xl border border-b-0 border-border bg-background shadow-[0_-1px_0_hsl(0_0%_100%),0_20px_40px_-24px_hsl(0_0%_0%/0.25)] ${className}`}>{children}</div>
);

/* ── Mock 1: catch up — threads being summarised ──────────────────────────── */
const THREADS = [
  { ch: "slack", title: "#launch — overnight thread", tags: ["Decision", "Blocker"] },
  { ch: "gmail", title: "Acme Co — invoice and renewal", tags: ["Money"] },
  { ch: "teams", title: "Finance — Q3 sign-off", tags: ["Approval"] },
  { ch: "outlook", title: "Priya — Thursday planning", tags: ["Meeting"] },
  { ch: "instagram", title: "@northwind — shipping question", tags: ["FAQ"] },
];
const TAG_TINT: Record<string, string> = {
  Decision: "bg-[hsl(216_90%_94%)] text-[hsl(216_84%_36%)]",
  Blocker: "bg-[hsl(0_80%_95%)] text-[hsl(0_70%_40%)]",
  Money: "bg-[hsl(140_45%_92%)] text-[hsl(150_60%_26%)]",
  Approval: "bg-[hsl(40_90%_92%)] text-[hsl(32_90%_32%)]",
  Meeting: "bg-[hsl(262_70%_95%)] text-[hsl(262_50%_42%)]",
  FAQ: "bg-[hsl(40_9%_93%)] text-[hsl(0_0%_30%)]",
};

export const SummariesMock = () => {
  const { ref, playing } = usePlaying<HTMLDivElement>();
  const i = useLoop(THREADS.length, 1900, playing);
  return (
    <div ref={ref} aria-hidden className="absolute inset-x-6 bottom-0 top-2 sm:inset-x-7">
      <Panel className="h-full p-5 sm:p-6">
        <p className="flex items-center gap-2 text-xl font-bold tracking-[-0.02em] text-foreground sm:text-2xl">
          <Sparkles className="h-5 w-5 text-[hsl(216_84%_52%)]" /> Catch-up
        </p>
        <div className="mt-3 flex gap-1 text-[12px]">
          <span className="rounded-md bg-[hsl(40_9%_95%)] px-2 py-1 font-medium">Since yesterday</span>
          <span className="px-2 py-1 text-muted-foreground">This week</span>
        </div>
        <div className="mt-3 divide-y divide-border border-y border-border">
          {THREADS.map((t, k) => (
            <div key={t.title} className={`flex items-center gap-2.5 py-2 transition-colors duration-300 ${k === i ? "bg-[hsl(40_9%_97%)]" : ""}`}>
              <img src={integrationByKey(t.ch)!.logo} alt="" className="h-4 w-4 shrink-0 object-contain" />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{t.title}</span>
              {t.tags.map((tag) => (
                <span key={tag} className={`hidden shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium sm:inline ${TAG_TINT[tag]}`}>{tag}</span>
              ))}
            </div>
          ))}
        </div>
      </Panel>
      <div className="absolute inset-x-4 bottom-5 flex items-center gap-3 rounded-full border border-border bg-background px-5 py-3 shadow-[0_12px_30px_-12px_hsl(0_0%_0%/0.3)] sm:inset-x-10">
        <span className="min-w-0 flex-1 truncate text-[15px] text-foreground">
          <span className="text-muted-foreground">Summarising </span>
          {THREADS[i].title.split(" — ")[0]}
        </span>
        <span className="flex h-5 items-center gap-[3px]">
          {[0, 1, 2, 3, 4, 5, 6].map((b) => (
            <span key={b} className="cm-bar w-[3px] rounded-full bg-foreground" style={{ animationDelay: `${b * 90}ms`, animationPlayState: playing ? "running" : "paused" }} />
          ))}
        </span>
      </div>
    </div>
  );
};

/* ── Mock 2: ask anything — a question types itself, the answer cites sources ─ */
const QA = [
  { q: "What did Acme say about the invoice?", a: "Dana confirmed invoice #2041 for $4,200 is due Friday and asked for the PO number.", cites: [["gmail", "Dana · Acme Co"], ["slack", "#finance"]] },
  { q: "Anything blocking the launch?", a: "One blocker: the pricing page copy is waiting on your approval. Three other items were decided.", cites: [["slack", "#launch"], ["teams", "Leo · Finance"]] },
  { q: "When am I meeting Priya?", a: "Thursday at 3:00 PM. The invite is already on your calendar.", cites: [["outlook", "Priya Shah"], ["google-calendar", "Calendar"]] },
];

export const AskMock = () => {
  const { ref, playing } = usePlaying<HTMLDivElement>();
  const [n, setN] = useState(0);
  const [typed, setTyped] = useState(isPrerender() ? QA[0].q.length : 0);
  const item = QA[n];

  useEffect(() => {
    if (!playing) return;
    if (typed < item.q.length) {
      const t = setTimeout(() => setTyped((v) => v + 1), 38);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setN((v) => (v + 1) % QA.length);
      setTyped(0);
    }, 3400);
    return () => clearTimeout(t);
  }, [playing, typed, item.q.length]);

  const done = typed >= item.q.length;
  return (
    <div ref={ref} aria-hidden className="absolute inset-x-6 bottom-0 top-2 sm:inset-x-7">
      <Panel className="h-full p-5 sm:p-6">
        <p className="flex items-center gap-2 text-xl font-bold tracking-[-0.02em] text-foreground sm:text-2xl">
          <FileText className="h-5 w-5 text-[hsl(32_90%_45%)]" /> Deep Search
        </p>
        <div className="mt-4 min-h-[120px] pr-14">
          <AnimatePresence mode="wait">
            {done && (
              <motion.div key={n} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <p className="text-[14px] leading-relaxed text-foreground">{item.a}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {item.cites.map(([ch, label], k) => (
                    <motion.span
                      key={label}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.25 + k * 0.12 }}
                      className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[12px] text-muted-foreground"
                    >
                      <img src={integrationByKey(ch)!.logo} alt="" className="h-3.5 w-3.5 object-contain" /> {label}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Panel>
      {["gmail", "slack", "teams"].map((k, idx) => (
        <motion.span
          key={k}
          className="absolute flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-background shadow-[0_8px_20px_-10px_hsl(0_0%_0%/0.3)]"
          style={{ top: `${18 + idx * 22}%`, right: `${6 + (idx % 2) * 10}%` }}
          animate={playing ? { y: [0, -7, 0] } : undefined}
          transition={{ duration: 3.5 + idx, repeat: Infinity, ease: "easeInOut" }}
        >
          <img src={integrationByKey(k)!.logo} alt="" className="h-5 w-5 object-contain" />
        </motion.span>
      ))}
      <div className="absolute inset-x-4 bottom-5 flex items-center gap-3 rounded-2xl border border-border bg-background px-5 py-3 shadow-[0_12px_30px_-12px_hsl(0_0%_0%/0.3)] sm:inset-x-10">
        <span className="min-w-0 flex-1 truncate text-[15px] text-foreground sm:text-[17px]">
          {item.q.slice(0, typed)}
          <span className="cm-caret ml-px inline-block h-[1.05em] w-[2px] translate-y-[0.18em] bg-foreground" />
        </span>
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white transition-colors ${done ? "bg-[hsl(216_84%_52%)]" : "bg-[hsl(0_0%_80%)]"}`}>
          <ArrowUp className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
};

/* ── Mock 3: agents — a reply agent reports back in a side panel ──────────── */
const AGENT_LINES = [
  { who: "you", text: "Take a look at Needs you and handle anything you can." },
  { who: "agent", text: "Looking at what's waiting on you now." },
  { who: "note", text: "Drafted 3 replies in Needs you" },
  { who: "agent", text: "Acme's invoice is the most urgent. I drafted a reply confirming payment by Thursday. Want me to send it?" },
];

export const AgentsMock = () => {
  const { ref, playing } = usePlaying<HTMLDivElement>();
  const step = useLoop(AGENT_LINES.length + 2, 1500, playing);
  const shown = isPrerender() ? AGENT_LINES.length : Math.min(step + 1, AGENT_LINES.length);
  return (
    <div ref={ref} aria-hidden className="absolute inset-0 flex items-end justify-end gap-0 pl-6 pt-4 sm:pl-10">
      <Panel className="hidden h-[82%] w-[46%] rounded-tr-none p-5 sm:block">
        <p className="flex items-center gap-2 text-lg font-bold tracking-[-0.02em] text-foreground">
          <PenLine className="h-4 w-4 text-[hsl(32_90%_45%)]" /> Needs you
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { h: "New", n: 2, tint: "bg-[hsl(40_90%_95%)]", cards: ["Invoice #2041 due Friday", "Shipping to Canada?"] },
            { h: "Drafted", n: 3, tint: "bg-[hsl(216_90%_96%)]", cards: ["Q3 sign-off", "Thursday 3pm works"] },
          ].map((c) => (
            <div key={c.h} className={`rounded-lg p-2 ${c.tint}`}>
              <p className="mb-1.5 px-1 text-[11px] font-medium text-foreground">{c.h} <span className="text-muted-foreground">{c.n}</span></p>
              {c.cards.map((t, k) => (
                <motion.div
                  key={t}
                  className="mb-1.5 rounded-md border border-border bg-background p-2 text-[11px] text-foreground"
                  animate={playing && c.h === "Drafted" && k === 0 ? { scale: [1, 1.03, 1] } : undefined}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                >
                  {t}
                  <span className="mt-1 block text-[10px] text-muted-foreground">{c.h === "Drafted" ? "Reply agent" : "Priority agent"}</span>
                </motion.div>
              ))}
            </div>
          ))}
        </div>
      </Panel>
      <div className="h-[94%] w-full overflow-hidden rounded-tl-xl border border-b-0 border-r-0 border-border bg-background shadow-[0_20px_40px_-24px_hsl(0_0%_0%/0.3)] sm:w-[54%]">
        <div className="h-14 bg-[hsl(40_90%_93%)]" />
        <div className="-mt-6 px-5 sm:px-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-background bg-[hsl(32_90%_55%)] text-white"><PenLine className="h-5 w-5" /></span>
          <p className="mt-3 text-xl font-bold tracking-[-0.02em] text-foreground">Reply agent</p>
          <div className="mt-3 space-y-2.5 pb-5">
            {AGENT_LINES.slice(0, shown).map((l) => (
              <motion.div key={l.text} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {l.who === "you" ? (
                  <p className="ml-auto w-fit max-w-[85%] rounded-lg bg-[hsl(40_9%_95%)] px-3 py-2 text-[12px] text-foreground">{l.text}</p>
                ) : l.who === "note" ? (
                  <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground"><Check className="h-3.5 w-3.5" /> {l.text}</p>
                ) : (
                  <p className="text-[13px] leading-relaxed text-foreground">{l.text}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Use-case cards: round colour icon, title, arrow ───────────────────────── */
export const UseCaseCard = ({ icon, tint, title, href }: { icon: ReactNode; tint: string; title: string; href: string }) => (
  <SmartLink href={href} className="cm-card cm-card-hover group flex h-full flex-col p-6">
    <span className={`flex h-10 w-10 items-center justify-center rounded-full text-white transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105 ${tint}`}>{icon}</span>
    <span className="mt-7 text-[17px] font-semibold leading-snug tracking-[-0.01em] text-foreground">
      {title} <ArrowRight className="inline h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    </span>
  </SmartLink>
);
